import React, {useState} from 'react';
import {
    BoxDiv,
    CheckboxDiv,
    ContentWrapper,
    FolderParentName,
    Label,
    ParentRolesWrapper,
    RoleListLabel,
    RolesOnFolder,
    RolesSelectionWrapper,
    SearchFieldContentWrapper,
    SearchFieldWrapper,
} from './AdvancedRolePicker.style';
import {t} from 'i18next';
import {join} from 'lodash';
import {isNullOrEmpty, isValidStringArray} from '../../utils';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../models/TemplatePropertySettings';
import {isMandatoryComponent, isVisibleComponent} from '../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_ROLES
} from '../../init/storage_object_constants';
import {Valid} from '../validation/Valid';

export interface AdvancedRolesSelectionProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isOwnerModeEnabled: boolean;
    readonly dataSource: string[];
    readonly parentRoles: string[];
    readonly roles: string[];
    onChange: (roles: string[]) => void;
}

const getRolesDataSource = (allRoles: string[], selectedRoles: string[]): string[] => {
    let dataSource = allRoles ? allRoles : [];
    let checkedRoles = selectedRoles ? selectedRoles : [];

    if ((dataSource.length > 0) && (checkedRoles.length > 0)) {
        let roles: string[] = [...checkedRoles.sort()];
        let filteredSources: string[] = dataSource.filter(role => (!checkedRoles.includes(role))).sort();
        if (filteredSources.length > 0) {
            roles = roles.concat(filteredSources);
        }

        return roles;
    }

    return dataSource;
};

/**
 * @name AdvancedRolesPicker
 * @description
 * Component to make a selection of string roles that can describe an item
 */
export const AdvancedRolesPicker: React.VFC<AdvancedRolesSelectionProps> = (props) => {
    const [dataSource, setDataSource] = useState<string[]>(getRolesDataSource(props.dataSource, props.roles));
    const [searchRole, setSearchRole] = useState<string>('');
    const [filteredRoles, setFilteredRoles] = useState<string[]>(dataSource);

    const saveRole = (value: string) => {
        let userRoles = props.roles.slice();
        let keyIndex = userRoles.indexOf(value);
        if (keyIndex === -1) {
            userRoles.push(value);
        } else {
            userRoles.splice(keyIndex, 1);
        }

        setDataSource(getRolesDataSource(props.dataSource, userRoles));
        props.onChange(userRoles);
    };

    const isSelected = (value: string): boolean => {
        return props.roles.includes(value);
    };

    const handleInput = (value: string) => {
        saveRole(value);
    };

    const handleOnChangeSearchRole = (value: string) => {
        let searchRoleValue = value.trim();
        setSearchRole(searchRoleValue);
        if (!isNullOrEmpty(searchRoleValue)) {
            let regex = new RegExp(`${searchRoleValue}`, 'i')
            setFilteredRoles(dataSource.filter(role => regex.test(role)));
        } else {
            setFilteredRoles(dataSource);
        }
    };

    return (
        <ContentWrapper id={generateIdentifier()}>
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                    && ((!props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings))
                        || (props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings)))
                    && (props.parentRoles.length > 0)) &&
                <Valid
                    isValid={(((!props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings))
                        || (props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings))) ? isValidStringArray(props.parentRoles) : true)}
                    message={((!props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings))
                        || (props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings))) ? t('ERROR_PARENT_ROLES_MISSING') : t('ERROR_PARENT_OWNER_ROLES_MISSING')}>
                    <ContentWrapper id={generateIdentifier()}>
                        <ParentRolesWrapper id={generateIdentifier()}>
                            <FolderParentName
                                id={generateIdentifier()}>{t('PARENT_ROLE_LABEL_COMPONENT')}</FolderParentName>
                            <RolesOnFolder id={generateIdentifier()}>{join(props.parentRoles, ', ')}</RolesOnFolder>
                        </ParentRolesWrapper>
                    </ContentWrapper>
                </Valid>
            }
            <ContentWrapper id={generateIdentifier()}>
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && ((!props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings))
                            || (props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings)))
                        && (dataSource.length === 0)) &&
                    <Label id={generateIdentifier()} htmlFor={'no-roles-available'}>{t('ROLES_NOT_AVAILABLE')}</Label>
                }
                {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && ((!props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings))
                            || (props.isOwnerModeEnabled && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings)))
                        && (dataSource.length > 0)) &&
                    <SearchFieldContentWrapper id={generateIdentifier()}>
                        <SearchFieldWrapper id={generateIdentifier()}
                                            key='searchField'
                                            className='a-text-field a-text-field--search'>
                            <input id={generateIdentifier('role-search', '-')}
                                   className='searchInputField'
                                   type='role-search'
                                   placeholder={t('ROLES_SEARCH_TERM')}
                                   value={searchRole}
                                   onChange={(event) => handleOnChangeSearchRole(event.target.value)}/>
                        </SearchFieldWrapper>
                        <RoleListLabel id={generateIdentifier()}
                                       htmlFor={'roles-available'}>{t('ROLES_AVAILABLE')}
                        </RoleListLabel>
                        <Valid
                            isValid={(((!props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings))
                                || (props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings))) ? isValidStringArray(props.roles) : true)}
                            message={((!props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings))
                                || (props.isOwnerModeEnabled && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings))) ? t('ERROR_ROLES_MISSING') : t('ERROR_OWNER_ROLES_MISSING')}>
                            <RolesSelectionWrapper id={generateIdentifier()}>
                                <BoxDiv id={generateIdentifier()} className='a-box -floating-shadow-s'>
                                    {filteredRoles?.map(role => {
                                        return <CheckboxDiv id={generateIdentifier()}
                                                            key={role}
                                                            className='a-checkbox'>
                                            <input id={role}
                                                   type='checkbox'
                                                   name='unselected Checkbox'
                                                   checked={isSelected(role)}
                                                   onClick={() => handleInput(role)}/>
                                            <Label id={generateIdentifier()} htmlFor={role}>{role}</Label>
                                        </CheckboxDiv>
                                    })}
                                </BoxDiv>
                            </RolesSelectionWrapper>
                        </Valid>
                    </SearchFieldContentWrapper>
                }
            </ContentWrapper>
        </ContentWrapper>
    );
};
